/** Interface size (the magnifier, 2026-09-09): a teacher with compromised sight scales the
 * whole app, not a lens over part of it.
 *
 * Every size in the app is in rem -- Tailwind's utilities, the control scale
 * (--control-h) and the fluid shell (--shell-max) -- so one number on the root does it:
 * styles.css sets `html { font-size: calc(16px * var(--ui-scale)) }`. Breakpoints stay in px,
 * so the layout reflows into the columns it would use on a smaller screen instead of
 * overflowing. Two places do not scale on purpose: the projector route, which is sized for the
 * room and never for the person at the keyboard (`applies()` below), and the hairlines and
 * radii, which are px because they are edges, not reading.
 *
 * Platform: the same code in the browser and in the desktop shell -- there is no native API to
 * fall back to, only the browser's own zoom, which the shortcut handler pre-empts where the
 * engine allows it (Chromium, Gecko; Safari keeps Cmd +/- for itself, and its page zoom then
 * does the same job). The setting is per device and survives a launch. */
import { APP_BASE } from "../api/base";
import { nsKey } from "./storage";

/** Steps a teacher can land on. 0.9 for a teacher who wants more on screen, 2 for one who
 * needs the type twice as large; beyond 2 the live screens stop fitting a question and its
 * options together, which is worse than a smaller type. */
export const ZOOM_STEPS = [0.9, 1, 1.15, 1.3, 1.5, 1.75, 2] as const;
export const ZOOM_MIN = ZOOM_STEPS[0];
export const ZOOM_MAX = ZOOM_STEPS[ZOOM_STEPS.length - 1] as number;
export const ZOOM_DEFAULT = 1;

/** `<slug>.ui.zoom`, read by index.html before first paint like the language and the
 * theme; "app.zoom" is what earlier builds wrote, moved on first read (STORY-138). */
const KEY = nsKey("ui.zoom", "app.zoom");
const EVENT = "ui-zoom-change";

const clamp = (z: number): number => (Number.isFinite(z) ? Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, z)) : ZOOM_DEFAULT);

/** The projector window shares this device's storage but not its reader: it is read from the
 * back of the room, so the teacher's setting must not touch it. */
function applies(): boolean {
  return typeof document !== "undefined" && !window.location.pathname.startsWith(`${APP_BASE}/p/`);
}

function stored(): number {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) return clamp(Number.parseFloat(raw));
  } catch {
    /* private mode: the setting simply does not persist */
  }
  return ZOOM_DEFAULT;
}

let level = typeof window === "undefined" ? ZOOM_DEFAULT : stored();

export function zoom(): number {
  return level;
}

function paint(z: number): void {
  if (!applies()) return;
  document.documentElement.style.setProperty("--ui-scale", String(z));
}

/** Set, persist and tell the UI. Returns the level actually used (clamped to the range). */
export function setZoom(z: number): number {
  level = clamp(z);
  paint(level);
  try {
    localStorage.setItem(KEY, String(level));
  } catch {
    /* ignore */
  }
  window.dispatchEvent(new CustomEvent(EVENT));
  return level;
}

/** One step up (+1) or down (-1) the scale; stops at the ends. */
export function stepZoom(direction: 1 | -1): number {
  const steps = ZOOM_STEPS as readonly number[];
  const here = steps.findIndex((s) => Math.abs(s - level) < 0.001);
  if (here === -1) {
    // A level from an older build or a hand-edited storage value: move to the nearest step.
    const nearer = direction === 1 ? steps.find((s) => s > level) : [...steps].reverse().find((s) => s < level);
    return setZoom(nearer ?? level);
  }
  return setZoom(steps[Math.min(steps.length - 1, Math.max(0, here + direction))]!);
}

export function resetZoom(): number {
  return setZoom(ZOOM_DEFAULT);
}

export function subscribeZoom(fn: () => void): () => void {
  window.addEventListener(EVENT, fn);
  return () => window.removeEventListener(EVENT, fn);
}

/** Apply what was stored (index.html already did it before first paint; this covers the
 * desktop shell's boot page, a route change into the app and a stale inline value). */
export function applyStoredZoom(): void {
  if (typeof window === "undefined") return;
  level = stored();
  paint(level);
}

/** Cmd/Ctrl + and - and 0, in the browser and in the desktop shell. Returns the remover.
 *
 * The key is read from `event.code` where the engine gives one, because on a Russian or any
 * non-Latin layout `event.key` for the minus and equals keys is still punctuation but on some
 * layouts is not, and Digit0 is the only reliable name for the reset key. */
export function installZoomShortcuts(): () => void {
  const onKey = (e: KeyboardEvent): void => {
    if (!(e.metaKey || e.ctrlKey) || e.altKey) return;
    const plus = e.code === "Equal" || e.code === "NumpadAdd" || e.key === "+" || e.key === "=";
    const minus = e.code === "Minus" || e.code === "NumpadSubtract" || e.key === "-" || e.key === "_";
    const zero = e.code === "Digit0" || e.code === "Numpad0" || e.key === "0";
    if (!plus && !minus && !zero) return;
    e.preventDefault(); // pre-empt the browser's own page zoom where the engine allows it
    if (plus) stepZoom(1);
    else if (minus) stepZoom(-1);
    else resetZoom();
  };
  window.addEventListener("keydown", onKey);
  return () => window.removeEventListener("keydown", onKey);
}
